import React from 'react';
import {Box, Stack, Tooltip, Typography} from '@mui/material';
import dayjs from 'dayjs';
import {useNavigate} from 'react-router';
import PriorityIndicator from '../../collection-requests/components/PriorityIndicator';
import AuthorChip from '../../products/components/common/AuthorChip';
import {coverageScoreColor, coverageTrackColor} from '../../collection-requests/coverage/coverageScoreColor';

function coverageColor(pct) {
    if (pct == null) {
        return {track: '#eeeeee', fill: '#bdbdbd', label: '#9e9e9e'};
    }
    return {
        track: coverageTrackColor(pct),
        fill: coverageScoreColor(pct),
        label: coverageScoreColor(pct),
    };
}

function CoverageRow({label, pct}) {
    const {track, fill, label: pctColor} = coverageColor(pct);
    const fillWidth = pct == null ? 0 : Math.max(Math.min(pct, 100), pct > 0 ? 2 : 0);
    return (
        <Tooltip title={pct != null ? `${label} ${pct}%` : `No ${label.toLowerCase()} coverage`} arrow>
            <Stack direction="row" spacing={1} alignItems="center" sx={{width: '100%', minWidth: 0}}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{minWidth: 52, flexShrink: 0, lineHeight: 1.2}}
                >
                    {label}
                </Typography>
                <Stack spacing={0.35} sx={{flex: 1, minWidth: 0}}>
                    <Typography
                        variant="caption"
                        fontSize="0.62rem"
                        fontWeight={700}
                        fontFamily='"Roboto Mono", monospace'
                        sx={{color: pctColor}}
                    >
                        {pct != null ? `${pct}%` : '—'}
                    </Typography>
                    <Box sx={{width: '100%', height: 3, bgcolor: track, borderRadius: 1, overflow: 'hidden'}}>
                        {fillWidth > 0 && (
                            <Box sx={{width: `${fillWidth}%`, height: '100%', bgcolor: fill}}/>
                        )}
                    </Box>
                </Stack>
            </Stack>
        </Tooltip>
    );
}

function DueDateCell({dueDate, status}) {
    if (!dueDate) {
        return (
            <Typography variant="caption" color="text.secondary" fontFamily='"Roboto Mono", monospace'>
                —
            </Typography>
        );
    }
    const parsed = dayjs(dueDate);
    if (!parsed.isValid()) {
        return (
            <Typography variant="caption" color="text.secondary" fontFamily='"Roboto Mono", monospace'>
                —
            </Typography>
        );
    }
    return (
        <Typography
            variant="caption"
            fontWeight={600}
            fontFamily='"Roboto Mono", monospace'
            sx={{color: status === 'expired' ? 'error.main' : 'text.secondary'}}
        >
            {parsed.format('YYYY-MM-DD')}
        </Typography>
    );
}

/**
 * Search-page-style EEI card for the IIR left panel and submitted IIR viewer.
 */
export default function LinkedEeiCard({item}) {
    const navigate = useNavigate();
    const number = item.number || item.eeiNumber || item.id;
    const userId = item.authorUserId || null;
    const displayName = item.authorDisplayName || item.author || null;

    const open = () => {
        if (!number) return;
        navigate(`/icr/eei/${encodeURIComponent(number)}`);
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
        }
    };

    return (
        <Stack
            role="button"
            tabIndex={0}
            onClick={open}
            onKeyDown={onKeyDown}
            bgcolor="white"
            borderRadius={2}
            outline="1px solid #cde"
            spacing={1}
            p={1.5}
            sx={{
                cursor: 'pointer',
                transition: 'box-shadow 0.2s ease',
                '&:hover': {
                    zIndex: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                },
            }}
        >
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{minWidth: 0}}>
                <Box sx={{flexShrink: 0, pt: '0.15em', lineHeight: 0}}>
                    <PriorityIndicator
                        priority={item.priority}
                        showLabel={false}
                        sx={{height: 18, width: 20}}
                    />
                </Box>
                <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        lineHeight: 1.35,
                        minWidth: 0,
                        flex: 1,
                    }}
                >
                    {item.question || item.text || '—'}
                </Typography>
            </Stack>
            <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography
                    variant="caption"
                    fontWeight={700}
                    fontFamily='"Roboto Mono", monospace'
                    color="text.secondary"
                >
                    {number || '—'}
                </Typography>
                {(userId || displayName) ? (
                    <Box
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            minWidth: 0,
                            '& > .MuiStack-root': {minWidth: 0, alignItems: 'flex-start'},
                            '& .MuiLink-root, & span.MuiTypography-root, & a, & span': {
                                whiteSpace: 'normal !important',
                                overflow: 'visible !important',
                                textOverflow: 'clip !important',
                                wordBreak: 'break-word',
                                lineHeight: 1.25,
                                fontSize: '0.75rem',
                            },
                        }}
                    >
                        <AuthorChip
                            userId={userId}
                            userDisplayName={displayName || userId || 'Unknown'}
                            flex={undefined}
                            avatarProfile={item.authorProfile || null}
                        />
                    </Box>
                ) : (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                )}
                <DueDateCell dueDate={item.dueDate} status={item.status}/>
            </Stack>
            <Stack spacing={0.75}>
                <CoverageRow label="Data" pct={item.dataCoveragePct}/>
                <CoverageRow label="Analytic" pct={item.analyticCoveragePct}/>
            </Stack>
        </Stack>
    );
}
